import { useQuery } from '@tanstack/react-query'
import { apiInstance } from 'services/api'
import dayjs, { Dayjs } from 'dayjs'
import { useMasquaradeToken } from 'services/masquarade/masquarade.hooks'
import { useAccessToken } from 'services/token/token.hooks'
import { LeaseContractResponse } from 'services/contracts/hooks/useLeaseContracts'
import { CONTRACT_DOCUMENT_TYPE, ContractDocumentTypes } from 'services/contracts/contracts.consts'
import { useTermsOfUse } from 'services/terms/terms.hooks'

export interface ParentInvoice {
  id: number
  number: string
  dateOfIssue: string
  file: string | null
}

export interface LeaseContractsInvoicesListResponse {
  id: number
  number: string
  totalAmountGross: string
  paidAmount: string
  dateOfIssue: string
  discount: number
  items: {
    id: number
    title: string
    amountGross: string
    amount: string
  }[]
  file: string | null
  isPaid: boolean
  parent?: ParentInvoice
}

export interface LeaseContractsInvoicesListWithDate
  extends Omit<LeaseContractsInvoicesListResponse, 'dateOfIssue'> {
  displayDate: Dayjs
  type: ContractDocumentTypes
}

export const useLeaseContractsInvoicesList = (
  id?: LeaseContractResponse['id'],
  isPaid: boolean = false
) => {
  const { accessToken } = useAccessToken()
  const { masquaradeToken } = useMasquaradeToken()
  const { hasUserConsented } = useTermsOfUse()

  const { data: leaseContractsInvoicesList = [], ...rest } = useQuery({
    queryKey: ['leaseContractsInvoicesList', id, isPaid, masquaradeToken],
    cacheTime: 3600000,
    queryFn: () =>
      apiInstance<LeaseContractsInvoicesListResponse[]>({
        url: `/lease/contracts/${id}/invoices/`,
        method: 'GET',
        headers: {
          authorization: `Bearer ${accessToken}`,
          'mask-authorization': !!masquaradeToken ? `Token ${masquaradeToken}` : undefined
        },
        params: {
          isPaid
        }
      }).then(({ data }) => data),
    select: (date) =>
      date.map((invoice) => ({
        ...invoice,
        displayDate: dayjs(invoice.dateOfIssue).startOf('month'),
        type: CONTRACT_DOCUMENT_TYPE.invoice
      })) as LeaseContractsInvoicesListWithDate[],
    enabled: !!accessToken && !!id && hasUserConsented
  })

  return { leaseContractsInvoicesList, ...rest }
}
